import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputOtp } from "primereact/inputotp";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import AuthService from "../../../../../services/authService";
import * as yup from "yup";
import { requestForToken } from "../../../../../Firebase/firebase";
import { ensureFirebaseChatAuth } from "../../../../../services/firebaseChatAuth";
import RecaptchaCheckbox from "../../../../../components/RecaptchaCheckbox";

// reCAPTCHA v2 site key (public). Unset disables the widget, so a missing Vercel
// env var can't lock staff out of the panel; enforcement lives in mohtaj-api.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// List of common free email domains
const freeEmailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
];

export default function SignIn({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showOTP, setShowOTP] = useState(false);
  const [isCompanyLogin, setIsCompanyLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const toast = useRef(null);

  // Detect if it's a company login
  useEffect(() => {
    if (location.pathname === "/company_sign_in") setIsCompanyLogin(true);
  }, [location.pathname]);

  // Form validation schema
  const schema = yup.object({
    email: yup
      .string()
      .email("Invalid email")
      .required("Email is required")
      .test(
        "is-not-free-email",
        "Email must be from a specific domain",
        (value) => {
          if (!value) return false;
          const domain = value.split("@")[1];
          return !freeEmailDomains.includes(domain);
        }
      ),
    password: yup.string().required("Password field is required."),
  });

  const {
    register,
    setValue,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  // Handle form submission
  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (!showOTP && data.email && isCompanyLogin) {
        setShowOTP(true);
        setLoading(false);
        return;
      }

      if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Please confirm you are not a robot.",
          life: 3000,
        });
        setLoading(false);
        return;
      }

      // Request FCM token with proper error handling
      let fcmToken = null;
      try {
        fcmToken = await requestForToken();
        if (fcmToken) {
          localStorage.setItem("fcm_token", fcmToken);
        }
      } catch (fcmError) {
        console.error("FCM token generation failed:", fcmError);
        // Don't show toast here, just log the error
        // FCM is optional, login should still work
      }

      // Prepare user data - Make fcm_token truly optional
      const userData = {
        email: data.email,
        password: data.password,
        platform_type: "web",
        role: "super-admin",
        ...(data.email_otp && { email_otp: data.email_otp }),
        ...(fcmToken && { fcm_token: fcmToken }), // Only include if exists
        ...(recaptchaToken && { "g-recaptcha-response": recaptchaToken }),
      };

      let response;
      try {
        response = await AuthService.login(userData);
      } finally {
        // The server has consumed the token whatever the outcome; a retry
        // needs a fresh challenge.
        recaptchaRef.current?.reset();
      }

      // The Sanctum bearer token from the login response is the session; it
      // must persist here because Api.js reads user.token to build the
      // Authorization header on every request.
      const localStorageData = {
        ...response?.data?.user,
        token: response?.data?.token,
        role: response?.data?.role,
      };

      // Save user data in localStorage
      if (response?.status === "success") {
        localStorage.setItem("user", JSON.stringify(localStorageData));

        // Exchange the fresh Sanctum session for a Firebase session so chat
        // has an identity to authorise against. Fired after the token is in
        // localStorage (Api.js reads it from there to build the Authorization
        // header) and deliberately not awaited — chat is one module, and a
        // chat-token failure must not block a staff member from logging in.
        // The Chat screen retries on mount via useFirebaseChatAuthReady.
        ensureFirebaseChatAuth().catch((chatAuthError) => {
          console.error("Firebase chat sign-in failed at login:", chatAuthError);
        });

        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: response?.message || "Login successful",
          life: 3000,
        });
        
        // Navigate after a short delay to show success message
        setTimeout(() => {
          navigate("/dashboard");
          // MOH-560: lift the logged-in user into App state; the reload was
          // only ever a workaround for App reading localStorage once.
          if (onLogin) {
            onLogin(localStorageData);
          } else {
            window.location.reload();
          }
        }, 1000);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response?.message || "Login failed. Please try again.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        "Login failed. Please check your credentials and try again.";
      
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-4">
      <Toast ref={toast} />
      <form
        onSubmit={onSubmit(handleSubmit)}
        className="flex min-h-[calc(100vh-200px)] flex-col justify-center items-center w-full sm:px-6"
      >
        <p className="font-semibold text-base text-[#5473E3] text-center mb-4">
          Login to the system
        </p>

        {/* Email Field */}
        <FloatLabel className="w-full max-w-md">
          <label htmlFor="email" className="block m-1">
            Email
          </label>
          <InputText
            {...register("email")}
            type="email"
            name="email"
            className={`block peer rounded-[5px] mt-3 w-full focus:outline-none focus:ring-1 ${
              errors.email
                ? "border-[#C93B32] focus:border-[#C93B32] focus:ring-[#C93B32]"
                : "border-[#AEBBCD]"
            }`}
          />
        </FloatLabel>
        {errors.email && (
          <span className="text-[14px] text-[#C93B32] mt-2 self-start max-w-md">
            {errors.email.message}
          </span>
        )}

        {/* OTP Field */}
        {showOTP && (
          <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:gap-5 w-full max-w-md">
            <label htmlFor="email_otp" className="block m-2 sm:m-0">
              Email OTP
            </label>
            <InputOtp
              {...register("email_otp")}
              onChange={(e) => setValue("email_otp", e.value)}
              integerOnly
              className={`block peer rounded-[5px] w-full focus:outline-none focus:ring-1 ${
                errors.email_otp
                  ? "border-[#C93B32] focus:border-[#C93B32] focus:ring-[#C93B32]"
                  : "border-[#AEBBCD]"
              }`}
            />
            {errors.email_otp && (
              <span className="text-[14px] text-[#C93B32] mt-2 sm:mt-0">
                {errors.email_otp.message}
              </span>
            )}
          </div>
        )}

        {/* Password Field */}
        {!isCompanyLogin && (
          <div className="w-full max-w-md mt-6">
            <FloatLabel>
              <Password
                {...register("password")}
                inputId="password"
                toggleMask
                inputStyle={{ width: "100%" }}
                onChange={(e) => setValue("password", e.target.value)}
                className={`block peer rounded-[5px] w-full mt-3 focus:outline-none focus:ring-1 ${
                  errors.password
                    ? "border-[#C93B32] focus:border-[#C93B32] focus:ring-[#C93B32]"
                    : "border-[#AEBBCD]"
                }`}
              />
              <label htmlFor="password">Password</label>
            </FloatLabel>
            {errors.password && (
              <span className="text-[14px] text-[#C93B32] mt-2 block">
                {errors.password.message}
              </span>
            )}
          </div>
        )}

        {RECAPTCHA_SITE_KEY && (
          <div className="w-full max-w-md mt-6 flex justify-center">
            <RecaptchaCheckbox
              ref={recaptchaRef}
              siteKey={RECAPTCHA_SITE_KEY}
              onChange={setRecaptchaToken}
            />
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="rounded-full bg-[#3D5FD9] text-[#F5F7FF] w-full max-w-md p-3 mt-6 hover:bg-[#2347C5] mb-5"
          disabled={loading}
        >
          {!loading ? "SIGN IN" : <i className="pi pi-spin pi-spinner"></i>}
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          © {new Date().getFullYear()} All rights reserved by GeekRedar.com
        </p>
      </form>
    </div>
  );
}